// Crypto Wallets → Configuration → Feature Flags (SM-267, split out of the
// old single /wallet-ops/config page). The master and per-capability
// kill-switches for the crypto rail. Mutations gate on CRYPTO_WALLET_CONFIG
// (server-enforced; the checkboxes also disable).
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { errMsg, useCanConfig } from "@/components/wallet-ops/configShared";
import {
  fetchCryptoFlags,
  setCryptoFlags,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoFlags } from "@/lib/wallet-types";

export const Route = createFileRoute("/_app/wallet-ops/config/flags")({
  component: FlagsConfigPage,
});

const FLAG_LABELS: { key: keyof CryptoFlags; label: string; hint: string }[] = [
  { key: "enabled", label: "Rail enabled", hint: "Master kill-switch for the whole crypto rail." },
  { key: "deposit_enabled", label: "Deposits", hint: "Detect + credit inbound deposits." },
  { key: "withdraw_enabled", label: "Withdrawals", hint: "Accept + execute withdrawal orders." },
  { key: "gas_funding_enabled", label: "Gas funding", hint: "Auto top-up deposit addresses with gas." },
  { key: "collection_enabled", label: "Collection", hint: "Sweep deposit addresses into the cold wallet." },
  { key: "manual_ops_enabled", label: "Manual ops", hint: "Allow operator-triggered manual interventions." },
];

function FlagsConfigPage() {
  const canConfig = useCanConfig();
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: walletKeys.flags(),
    queryFn: fetchCryptoFlags,
  });
  const [draft, setDraft] = useState<CryptoFlags | null>(null);
  useEffect(() => {
    if (flags.data) setDraft(flags.data);
  }, [flags.data]);

  const save = useMutation({
    mutationFn: (input: CryptoFlags) => setCryptoFlags(input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: walletKeys.flags() });
    },
    meta: { successMessage: "Feature flags saved." },
  });

  const dirty =
    draft !== null &&
    flags.data !== undefined &&
    JSON.stringify(draft) !== JSON.stringify(flags.data);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Feature Flags</h1>
        <p className="text-sm text-muted">
          Master and per-capability kill-switches for the crypto rail —
          deposits, withdrawals, gas funding, collection, and manual ops.
        </p>
      </div>
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Feature flags</CardTitle>
          {canConfig && draft && (
            <Button
              size="sm"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              Save flags
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {flags.isLoading && <Skeleton className="h-24" />}
          {flags.isError && (
            <p className="text-2xs text-no">{errMsg(flags.error)}</p>
          )}
          {draft && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {FLAG_LABELS.map(({ key, label, hint }) => (
                <label
                  key={key}
                  className="flex items-start gap-2.5 rounded-md border border-border p-3 text-sm text-t-2"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={draft[key]}
                    disabled={!canConfig}
                    onChange={(e) =>
                      setDraft({ ...draft, [key]: e.target.checked })
                    }
                  />
                  <span>
                    <span className="block font-medium text-fg">{label}</span>
                    <span className="block text-2xs text-muted">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
